import { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import Header from '../components/Header';
import LogService, { LOG_ACTIONS } from '../services/LogService';
import { supabase } from '../lib/supabase';

const ACTION_LABELS = {
  login_success: 'Login',
  login_fail: 'Login Falha',
  logout: 'Logout',
  register: 'Registro',
  upload: 'Upload',
  analysis_start: 'Análise Início',
  analysis_end: 'Análise Fim',
  analysis_error: 'Análise Erro',
  download_excel: 'Download Excel',
  download_pdf: 'Download PDF',
  view_plans: 'Ver Planos',
  payment_attempt: 'Pagamento',
  feedback_positive: 'Feedback +',
  feedback_negative: 'Feedback -',
  manual_item_add: 'Item Manual',
  consent: 'Consentimento',
  cub_calculate: 'Calculo CUB',
  cub_admin_update: 'CUB Atualizado',
  sinapi_search: 'SINAPI Busca',
  sinapi_view_composicao: 'SINAPI Detalhe',
  sinapi_add_to_orcamento: 'SINAPI + Orç',
  sinapi_remove_from_orcamento: 'SINAPI - Orç',
  sinapi_calculate: 'SINAPI Cálculo',
  sinapi_export_excel: 'SINAPI Excel',
  sinapi_export_pdf: 'SINAPI PDF',
  sinapi_import_start: 'SINAPI Import',
  sinapi_import_complete: 'SINAPI OK',
  sinapi_import_error: 'SINAPI Erro',
};

const PIE_COLORS = ['#1e40af', '#3b82f6', '#60a5fa', '#93c5fd', '#bfdbfe', '#2563eb', '#1d4ed8', '#1e3a8a', '#dbeafe', '#eff6ff', '#7c3aed', '#a78bfa'];

function getLast7Days() {
  const days = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    d.setHours(0, 0, 0, 0);
    days.push(d);
  }
  return days;
}

function formatShortDate(date) {
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

function formatFullDate(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString('pt-BR') + ' ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

function AdminDashboard() {
  const navigate = useNavigate();
  const logs = useMemo(() => LogService.getAll(), []);

  // KPIs — fetch user count from Supabase profiles
  const [totalUsers, setTotalUsers] = useState(0);
  useEffect(() => {
    supabase.from('profiles').select('id', { count: 'exact', head: true })
      .then(({ count }) => { if (count != null) setTotalUsers(count); });
  }, []);

  const todayStr = new Date().toISOString().slice(0, 10);

  const analysisTotal = useMemo(
    () => logs.filter((l) => l.action === LOG_ACTIONS.ANALYSIS_END).length,
    [logs]
  );

  const analysisToday = useMemo(
    () => logs.filter((l) => l.action === LOG_ACTIONS.ANALYSIS_END && l.timestamp.slice(0, 10) === todayStr).length,
    [logs, todayStr]
  );

  const successRate = useMemo(() => {
    const ends = logs.filter((l) => l.action === LOG_ACTIONS.ANALYSIS_END).length;
    const errors = logs.filter((l) => l.action === LOG_ACTIONS.ANALYSIS_ERROR).length;
    const total = ends + errors;
    return total === 0 ? 100 : Math.round((ends / total) * 100);
  }, [logs]);

  const totalDownloads = useMemo(
    () => logs.filter((l) => l.action === LOG_ACTIONS.DOWNLOAD_EXCEL || l.action === LOG_ACTIONS.DOWNLOAD_PDF).length,
    [logs]
  );

  // Charts data
  const last7 = getLast7Days();

  const usersPerDay = useMemo(() => {
    return last7.map((day) => {
      const dayStr = day.toISOString().slice(0, 10);
      const count = logs.filter((l) => l.action === LOG_ACTIONS.REGISTER && l.timestamp.slice(0, 10) === dayStr).length;
      return { name: formatShortDate(day), value: count };
    });
  }, [logs, last7]);

  const analysisPerDay = useMemo(() => {
    return last7.map((day) => {
      const dayStr = day.toISOString().slice(0, 10);
      const success = logs.filter((l) => l.action === LOG_ACTIONS.ANALYSIS_END && l.timestamp.slice(0, 10) === dayStr).length;
      const error = logs.filter((l) => l.action === LOG_ACTIONS.ANALYSIS_ERROR && l.timestamp.slice(0, 10) === dayStr).length;
      return { name: formatShortDate(day), sucesso: success, erro: error };
    });
  }, [logs, last7]);

  const actionDistribution = useMemo(() => {
    const counts = {};
    logs.forEach((l) => {
      const label = ACTION_LABELS[l.action] || l.action;
      counts[label] = (counts[label] || 0) + 1;
    });
    return Object.entries(counts)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);
  }, [logs]);

  // Recent activity
  const recentLogs = logs.slice(0, 10);

  const statusBadge = (status) => {
    if (status === 'success') return 'bg-green-100 text-green-700';
    if (status === 'error') return 'bg-red-100 text-red-700';
    return 'bg-blue-100 text-blue-700';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-6 md:py-10">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1">Dashboard</h1>
            <p className="text-gray-500">Visão geral do sistema</p>
          </div>
          <div className="flex gap-2 mt-3 sm:mt-0">
            <button
              onClick={() => navigate('/admin/logs')}
              className="px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
            >
              Logs
            </button>
            <button
              onClick={() => navigate('/admin/feedbacks')}
              className="px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
            >
              Feedbacks
            </button>
            <button
              onClick={() => navigate('/admin/usuarios')}
              className="px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
            >
              Usuários
            </button>
            <button
              onClick={() => navigate('/admin/cub')}
              className="px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
            >
              CUB
            </button>
            <button
              onClick={() => navigate('/admin/sinapi')}
              className="px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
            >
              SINAPI
            </button>
          </div>
        </div>
        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Usuários</span>
            </div>
            <p className="text-3xl font-bold text-gray-900">{totalUsers}</p>
            <p className="text-xs text-gray-400 mt-1">cadastrados</p>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
              </div>
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Análises</span>
            </div>
            <p className="text-3xl font-bold text-gray-900">{analysisToday} <span className="text-lg font-normal text-gray-400">/ {analysisTotal}</span></p>
            <p className="text-xs text-gray-400 mt-1">hoje / total</p>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Sucesso</span>
            </div>
            <p className="text-3xl font-bold text-gray-900">{successRate}%</p>
            <p className="text-xs text-gray-400 mt-1">taxa de sucesso</p>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
              </div>
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Downloads</span>
            </div>
            <p className="text-3xl font-bold text-gray-900">{totalDownloads}</p>
            <p className="text-xs text-gray-400 mt-1">Excel + PDF</p>
          </div>
        </div>

        {/* Charts */}
        <div className="grid lg:grid-cols-2 gap-6 mb-8">
          {/* Users per day - Line */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="text-sm font-semibold text-gray-800 mb-4">Novos usuários (7 dias)</h3>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={usersPerDay}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                  <Tooltip
                    contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13 }}
                    labelStyle={{ fontWeight: 600 }}
                  />
                  <Line type="monotone" dataKey="value" name="Usuários" stroke="#3b82f6" strokeWidth={2.5} dot={{ fill: '#3b82f6', r: 4 }} activeDot={{ r: 6 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Analyses per day - Bar */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="text-sm font-semibold text-gray-800 mb-4">Análises por dia (7 dias)</h3>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={analysisPerDay}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                  <Tooltip
                    contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13 }}
                    labelStyle={{ fontWeight: 600 }}
                  />
                  <Bar dataKey="sucesso" name="Sucesso" fill="#1e40af" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="erro" name="Erro" fill="#ef4444" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Pie + Recent Activity */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Pie chart */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="text-sm font-semibold text-gray-800 mb-4">Ações mais realizadas</h3>
            {actionDistribution.length === 0 ? (
              <p className="text-gray-400 text-sm text-center py-12">Sem dados</p>
            ) : (
              <>
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={actionDistribution}
                        cx="50%"
                        cy="50%"
                        innerRadius={40}
                        outerRadius={75}
                        paddingAngle={2}
                        dataKey="value"
                      >
                        {actionDistribution.map((_, i) => (
                          <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13 }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="space-y-1.5 mt-2">
                  {actionDistribution.slice(0, 5).map((item, i) => (
                    <div key={item.name} className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-2">
                        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }} />
                        <span className="text-gray-600">{item.name}</span>
                      </div>
                      <span className="font-medium text-gray-800">{item.value}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Recent activity */}
          <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-gray-800">Últimas atividades</h3>
              <button
                onClick={() => navigate('/admin/logs')}
                className="text-xs text-orca-600 hover:text-orca-800 font-medium cursor-pointer"
              >
                Ver todos
              </button>
            </div>
            {recentLogs.length === 0 ? (
              <p className="text-gray-400 text-sm text-center py-12">Nenhuma atividade registrada</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 text-left">
                      <th className="pb-2 font-medium text-gray-500 text-xs">Data/Hora</th>
                      <th className="pb-2 font-medium text-gray-500 text-xs">Email</th>
                      <th className="pb-2 font-medium text-gray-500 text-xs">Ação</th>
                      <th className="pb-2 font-medium text-gray-500 text-xs">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {recentLogs.map((log) => (
                      <tr key={log.id} className="hover:bg-gray-50 transition-colors">
                        <td className="py-2.5 text-gray-600 whitespace-nowrap text-xs">{formatFullDate(log.timestamp)}</td>
                        <td className="py-2.5 text-gray-700 max-w-[150px] truncate">{log.user_email}</td>
                        <td className="py-2.5">
                          <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-orca-100 text-orca-700">
                            {ACTION_LABELS[log.action] || log.action}
                          </span>
                        </td>
                        <td className="py-2.5">
                          <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${statusBadge(log.status)}`}>
                            {log.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

export default AdminDashboard;
